import { zodResolver } from "@hookform/resolvers/zod";
import { useUpdateVerificationsMutation } from "Api/Mutations/Verifications/useUpdateVerificationsMutation";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { StyledForm } from "Components/Users/UserDetailPageContent/Components/StyledForm";
import { VerificationFormFields } from "Components/Users/UserDetailPageContent/Components/VerificationForm/VerificationFormFields";
import {
  type UserVerificationUpdateFormData,
  verificationUpdateValidationSchema,
} from "Components/Users/schemas";
import type { UserVerificationUpdateDto } from "Infrastructure/Api/Api";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.Verification.Form.Controls;

type VerificationFormProps = {
  verification: UserVerificationUpdateDto;
  legacySourceOfIncome?: string | null;
};

export const VerificationForm = ({
  verification,
  legacySourceOfIncome,
}: VerificationFormProps) => {
  const { t } = useResource();
  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { isDirty },
  } = useForm<UserVerificationUpdateFormData>({
    resolver: zodResolver(verificationUpdateValidationSchema),
    defaultValues: {
      ...verification,
      uboUnknown: verification.uboUnknown ?? false,
    },
  });

  const { mutate: updateVerifications, isPending } =
    useUpdateVerificationsMutation({
      onSuccess: (data: UserVerificationUpdateDto) => {
        reset({ ...data, uboUnknown: data.uboUnknown ?? false });
        toast.success(t(COMPONENT_RESOURCES.SubmitSuccess));
      },
      onError: () => toast.error(t(COMPONENT_RESOURCES.SubmitError)),
    });

  const onSubmit = (data: UserVerificationUpdateFormData) => {
    updateVerifications(data);
  };

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <PrimaryPaper>
        <VerificationFormFields
          control={control}
          watch={watch}
          legacySourceOfIncome={legacySourceOfIncome}
        />
        <PrimaryButton
          fullWidth
          type="submit"
          disabled={!isDirty}
          isLoading={isPending}
          variant="contained"
          color="primary"
        >
          {t(COMPONENT_RESOURCES.Save)}
        </PrimaryButton>
      </PrimaryPaper>
    </StyledForm>
  );
};
